import { useNavigate, useParams } from "react-router";
import { Paper, Stack } from "@mui/material";
import { Department, User } from "../../../services/swagger";
import { useEffect, useState } from "react";
import { DepartmentService, UserService } from "../../../services/api";
import useRefresh from "../../../hooks/useRefresh";
import FormInput from "../../molecules/FormInput";
import FormTemplate from "../../templates/FormTemplate";
import { useTranslation } from "react-i18next";
import ItemList from "../../molecules/ItemList";
import DeleteIcon from "@mui/icons-material/Delete";
import useConfirm from "../../../hooks/useConfirm";
import { useMutation } from "@tanstack/react-query";

export default function EditDepartmentPage() {
  const { t } = useTranslation("Department");
  const { _id } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [administrative, setAdministrative] = useState<boolean>(false);
  const [users, setUsers] = useState<string[]>([]);
  const [userList, setUserList] = useState<User[]>([]);
  const [department, setDepartment] = useState<Department>();
  const confirm = useConfirm();

  const { mutate: loadUsers, isPending: isUserListLoading } = useMutation({
    mutationFn: async () => {
      const res = await UserService.getPrivilegedUserList({
        ignoreDeleted: true,
      });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) setUserList(data);
    },
  });

  const { mutate: loadDepartment, isPending: isDepartmentLoading } =
    useMutation({
      mutationFn: async () => {
        const res = await DepartmentService.getDepartment({
          id: _id || "",
        });
        return res?.data?.data;
      },
      onSuccess: (data) => {
        if (data) setDepartment(data);
      },
    });

  const refresh = useRefresh(loadDepartment);

  useEffect(() => {
    if (!department) return;
    setName(department.name);
    setDescription(department.description);
    setUsers(department.users.map((user) => user._id || ""));
    setAdministrative(department.administrative);
  }, [department]);

  useEffect(() => {
    loadDepartment();
    loadUsers();
  }, []);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleDelete = () => {
    confirm(async () => {
      if (!_id) return;
      const res = await DepartmentService.deleteDepartment({
        id: _id,
      });
      if (res?.data?.success) navigate(-1);
    });
  };

  const handleSubmit = async () => {
    if (!_id) return;
    const res = await DepartmentService.updateDepartment({
      id: _id,
      updateDepartmentBody: {
        name,
        description,
        users: users,
        administrative,
      },
    });
    if (res?.data?.success) navigate(-1);
  };

  return (
    <Paper sx={{ height: 1 }}>
      <FormTemplate
        title={`${t("Edit")} ${department?.name}`}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        options={[
          {
            label: t("Delete"),
            icon: <DeleteIcon color="error" />,
            onClick: handleDelete,
          },
        ]}
        confirm
      >
        <Stack spacing={2}>
          <FormInput
            loading={isDepartmentLoading}
            field="name"
            label={t("Name")}
            onChange={(v) => {
              setName(v as string);
            }}
            type="string"
            value={name}
            required
          />
          <FormInput
            loading={isDepartmentLoading}
            field="description"
            label={t("Description")}
            onChange={(v) => {
              setDescription(v as string);
            }}
            type="string"
            value={description}
          />
          <FormInput
            loading={isDepartmentLoading}
            field="administrative"
            label={t("Administrative")}
            value={administrative}
            type="boolean"
            onChange={(v) => setAdministrative(v as boolean)}
            required
          />
          <ItemList
            loading={isUserListLoading}
            reorderable={true}
            unique
            items={users}
            onChange={(list) => setUsers(list)}
            renderItem={(value) =>
              userList.find((user) => user._id === value)?.name
            }
            addList={userList
              .filter(
                (user) => !users.includes(user._id as string) && !user.deleted,
              )
              .map((user) => ({
                name: user.name,
                value: user._id || "",
              }))}
            label={t("Users")}
            emptyLabel={t("NoUserAssigned")}
          />
        </Stack>
      </FormTemplate>
    </Paper>
  );
}
